import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/lib/api-client';
import type { ExceptionStatus, MediaType, BusinessCategory } from '@/types';

export interface ExceptionQueueItem {
  id: string;
  holdRequestId: string;
  requestNumber: string;
  advertiser: string;
  businessCategory: BusinessCategory;
  salesAeId: string;
  salesAeName: string;
  region: string;
  displayName: string;
  market: string;
  mediaType: MediaType;
  holdStartDate: string;
  holdExpirationDate: string;
  flightStartDate: string;
  flightEndDate: string;
  exceptionStatus: ExceptionStatus;
  chartingStatus?: string;
  daysUntilExpiration?: number;
  createdAt: string;
  qty: number;
  digitalOnlyNoOfSpots?: number;
}

export interface ExceptionQueueFilters {
  exceptionStatus?: ExceptionStatus;
  region?: string;
  market?: string;
  mediaType?: MediaType;
  businessCategory?: BusinessCategory;
  salesAeId?: string;
  expiringWithinDays?: number;
  includeExpired?: boolean;
  sortBy?: 'expiration' | 'created' | 'region' | 'status';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface ExceptionStatistics {
  totalInReview: number;
  totalApproved: number;
  totalNotApproved: number;
  totalRevisionRequired: number;
  expiringToday: number;
  expiringThisWeek: number;
  byRegion?: Record<string, number>;
}

/**
 * Hook to fetch exceptions queue with enterprise-wide visibility
 * Requirements: 7.1, 7.2
 */
export function useExceptionsQueue(filters: ExceptionQueueFilters = {}) {
  const queryParams = new URLSearchParams();
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.append(key, String(value));
    }
  });

  return useQuery({
    queryKey: ['exceptionsQueue', filters],
    queryFn: async () => {
      const response = await get<{
        success: boolean;
        data: {
          items: ExceptionQueueItem[];
          pagination: {
            total: number;
            limit: number;
            offset: number;
            hasMore: boolean;
          };
        };
      }>(`/exceptions/queue?${queryParams.toString()}`);
      return response.data;
    },
  });
}

/**
 * Hook to fetch exception statistics
 * Requirements: 7.1, 7.2
 */
export function useExceptionStatistics(region?: string) {
  const queryParams = new URLSearchParams();
  if (region) {
    queryParams.append('region', region);
  }

  return useQuery({
    queryKey: ['exceptionStatistics', region],
    queryFn: async () => {
      const response = await get<{ success: boolean; data: ExceptionStatistics }>(
        `/exceptions/statistics?${queryParams.toString()}`
      );
      return response.data;
    },
  });
}

/**
 * Hook to fetch all regions for filtering
 * Requirements: 7.2
 */
export function useRegions() {
  return useQuery({
    queryKey: ['regions'],
    queryFn: async () => {
      const response = await get<{ success: boolean; data: string[] }>('/exceptions/regions');
      return response.data;
    },
  });
}

/**
 * Hook to approve exception hold
 * Requirements: 7.3
 */
export function useApproveException() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ lineItemId, comments }: { lineItemId: string; comments: string }) =>
      post<{ success: boolean; message: string }>('/exceptions/approve', {
        lineItemId,
        comments,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exceptionsQueue'] });
      queryClient.invalidateQueries({ queryKey: ['exceptionStatistics'] });
      queryClient.invalidateQueries({ queryKey: ['lineItems'] });
      queryClient.invalidateQueries({ queryKey: ['lineItemDetail'] });
      queryClient.invalidateQueries({ queryKey: ['holdRequests'] });
      queryClient.invalidateQueries({ queryKey: ['lineItemComments'] });
    },
  });
}

/**
 * Hook to reject exception hold
 * Requirements: 7.4
 */
export function useRejectException() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ lineItemId, reason }: { lineItemId: string; reason: string }) =>
      post<{ success: boolean; message: string }>('/exceptions/reject', {
        lineItemId,
        reason,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exceptionsQueue'] });
      queryClient.invalidateQueries({ queryKey: ['exceptionStatistics'] });
      queryClient.invalidateQueries({ queryKey: ['lineItems'] });
      queryClient.invalidateQueries({ queryKey: ['lineItemDetail'] });
      queryClient.invalidateQueries({ queryKey: ['holdRequests'] });
      queryClient.invalidateQueries({ queryKey: ['lineItemComments'] });
    },
  });
}

/**
 * Hook to bulk process exceptions (approve/reject)
 * Requirements: 7.3, 7.4
 */
export function useBulkProcessExceptions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (approvals: Array<{ lineItemId: string; approved: boolean; comments: string }>) =>
      post<{ success: boolean; message: string }>('/exceptions/bulk-process', { approvals }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exceptionsQueue'] });
      queryClient.invalidateQueries({ queryKey: ['exceptionStatistics'] });
      queryClient.invalidateQueries({ queryKey: ['lineItems'] });
      queryClient.invalidateQueries({ queryKey: ['lineItemDetail'] });
      queryClient.invalidateQueries({ queryKey: ['holdRequests'] });
      queryClient.invalidateQueries({ queryKey: ['lineItemComments'] });
    },
  });
}

/**
 * Hook to route extension request to exceptions team
 * Requirements: 7.5
 */
export function useRouteExtension() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ lineItemId, extensionDays }: { lineItemId: string; extensionDays: number }) =>
      post<{ success: boolean; message: string }>('/exceptions/route-extension', {
        lineItemId,
        extensionDays,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exceptionsQueue'] });
      queryClient.invalidateQueries({ queryKey: ['exceptionStatistics'] });
      queryClient.invalidateQueries({ queryKey: ['lineItems'] });
      queryClient.invalidateQueries({ queryKey: ['lineItemDetail'] });
      queryClient.invalidateQueries({ queryKey: ['lineItemComments'] });
    },
  });
}

/**
 * Hook to resubmit a rejected exception line item
 * Requirements: 12.5
 */
export function useResubmitException() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ lineItemId, updates }: { lineItemId: string; updates: Record<string, unknown> }) =>
      post<{ success: boolean; message: string }>(`/exceptions/resubmit/${lineItemId}`, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exceptionsQueue'] });
      queryClient.invalidateQueries({ queryKey: ['exceptionStatistics'] });
      queryClient.invalidateQueries({ queryKey: ['lineItems'] });
      queryClient.invalidateQueries({ queryKey: ['lineItemDetail'] });
      queryClient.invalidateQueries({ queryKey: ['holdRequests'] });
      queryClient.invalidateQueries({ queryKey: ['lineItemComments'] });
    },
  });
}
